(() => {
  const retryLabel = document.currentScript?.dataset.retryLabel || 'Load more results';
  // Each definition has its own Section Rendering request: Shopify's `page`
  // parameter cannot independently paginate multiple definitions in one render.
  const definitions = new Set(['suppliers', 'treatments', 'categories', 'treatment-categories', 'emotions']);
  const requests = new Map();
  const words = value => String(value || '').trim().split(/\s+/).filter(Boolean);
  const normalize = value => String(value || '').normalize('NFKD').replace(/[\u0591-\u05C7]/g, '').trim().toLocaleLowerCase();
  const ordered = records => [...records].sort((a, b) => Number(a.sort) - Number(b.sort));
  const card = (record, variant) => record.template.content.querySelector(`[data-catalog-card="${variant}"]`)?.firstElementChild?.cloneNode(true);

  const load = key => {
    if (!definitions.has(key)) return Promise.reject(new Error('Unknown Spirit catalog'));
    if (requests.has(key)) return requests.get(key);
    const request = (async () => {
      let url = new URL(window.location.href);
      url.searchParams.set('page', '1');
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', `spirit-catalog-${key}`);
      const seen = new Set();
      const records = new Map();
      while (url) {
        if (url.origin !== window.location.origin || seen.has(url.href)) throw new Error('Invalid Spirit catalog pagination');
        seen.add(url.href);
        const response = await fetch(url.href, { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`Spirit catalog: ${response.status}`);
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const page = parsed.querySelector('[data-spirit-catalog-page]');
        if (page?.dataset.spiritCatalogPage !== key) throw new Error('Missing Spirit catalog section');
        page.querySelectorAll('template[data-catalog-record]').forEach(template => {
          const record = { ...template.dataset, template };
          if (record.id) records.set(record.id, record);
        });
        if (!page.dataset.next) break;
        const next = new URL(page.dataset.next, url);
        // paginate.next can contain only ?page=2; retain preview, locale and view.
        url.searchParams.forEach((value, name) => {
          if (!next.searchParams.has(name)) next.searchParams.set(name, value);
        });
        next.searchParams.set('section_id', `spirit-catalog-${key}`);
        url = next;
      }
      return [...records.values()];
    })().catch(error => {
      requests.delete(key);
      throw error;
    });
    requests.set(key, request);
    return request;
  };

  const emotionCategories = (emotion, categories) => {
    if (!emotion) return [];
    const legacy = { release: 'עומס ולחץ', connection: 'חיבור לעצמי', clarity: 'חיפוש כיוון', growth: 'העמקה וצמיחה', quiet: 'רוגע ושקט פנימי' };
    const aliases = [emotion.name, emotion.subtitle, emotion.support, emotion.displayName, emotion.handle, legacy[emotion.handle]].map(normalize).filter(Boolean);
    const direct = words(emotion.categories);
    return categories.filter(category => direct.includes(category.id) || words(category.emotions).includes(emotion.handle) ||
      [category.primaryHandle, category.secondaryHandle].includes(emotion.handle) ||
      [category.primary, category.secondary].map(normalize).filter(Boolean).some(value =>
        aliases.includes(value) || (normalize(emotion.name) && (value.includes(normalize(emotion.name)) || normalize(emotion.name).includes(value)))));
  };

  const categoryMatches = (category, term, handle) => {
    const keys = [category.name, category.code, category.handle, category.primary, category.secondary].map(normalize).filter(Boolean);
    return keys.includes(normalize(term)) || (!!handle && keys.includes(normalize(handle)));
  };

  const searchCategories = async term => {
    const normalized = normalize(term).replace(/^product_type:event\s*/, '');
    if (!normalized) return [];
    return (await load('categories')).filter(category => categoryMatches(category, normalized) ||
      (normalized.length > 2 && [category.primary, category.secondary].some(value => normalize(value).includes(normalized))));
  };

  const eventSources = new Map();
  const categorySearchItems = async categories => {
    const handles = new Set(categories.map(category => category.handle));
    if (!handles.size) return [];
    const paths = [...new Set(categories.map(category => category.collection || `${window.Shopify?.routes?.root || '/'}collections/all`))];
    const results = await Promise.all(paths.map(path => {
      if (!eventSources.has(path)) {
        eventSources.set(path, (async () => {
          let url = new URL(path, window.location.origin);
          url.searchParams.set('section_id', 'spirit-result-products');
          url.searchParams.set('page', '1');
          const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
          if (preview) url.searchParams.set('preview_theme_id', preview);
          const seen = new Set();
          const items = new Map();
          while (url) {
            if (url.origin !== window.location.origin || seen.has(url.href)) throw new Error('Invalid category event pagination');
            seen.add(url.href);
            const response = await fetch(url.href, { credentials: 'same-origin' });
            if (!response.ok) throw new Error('Category events unavailable');
            const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
            const source = parsed.querySelector('[data-spirit-collection-search-source]')?.content;
            if (!source && !parsed.querySelector('[data-spirit-results]')) throw new Error('Missing category event section');
            source?.querySelectorAll('[data-spirit-collection-search-item]').forEach(item => items.set(item.dataset.spiritProductId, item));
            const next = source?.querySelector('[data-spirit-collection-source-next]')?.getAttribute('href');
            if (!next) break;
            const nextUrl = new URL(next, url);
            url.searchParams.forEach((value, name) => { if (!nextUrl.searchParams.has(name)) nextUrl.searchParams.set(name, value); });
            url = nextUrl;
          }
          return [...items.values()];
        })().catch(error => { eventSources.delete(path); throw error; }));
      }
      return eventSources.get(path);
    }));
    return results.flat().filter(item => (item.dataset.spiritEventCategories || '').split(',').some(handle => handles.has(handle)));
  };

  const addOptions = (select, records, value, extra) => {
    if (!select) return;
    const selected = select.value;
    records.forEach(record => {
      const key = value(record);
      let option = [...select.options].find(item => item.value === key);
      if (!option) { option = new Option(record.name, key); select.append(option); }
      if (extra) extra(option, record);
    });
    select.value = selected;
  };

  const enrichDirectory = async root => {
    const [treatments, categories] = await Promise.all([
      load('treatments'), load('treatment-categories')
    ]);
    if (!root.isConnected) return;
    addOptions(root.querySelector('[data-directory-treatment]'), ordered(treatments), record => record.key);
    const select = root.querySelector('[data-directory-category]');
    addOptions(select, ordered(categories), record => record.key);
    const group = root.querySelector('.spirit-directory__category-options');
    categories.forEach(record => {
      if (!group || group.querySelector(`[data-value="${CSS.escape(record.key)}"]`)) return;
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.directoryCategoryOption = '';
      button.className = 'spirit-directory__category-option';
      button.dataset.spiritCategory = record.key;
      button.dataset.value = record.key;
      button.dataset.label = record.name;
      const label = document.createElement('span');
      label.textContent = record.name;
      button.append(label);
      button.setAttribute('aria-pressed', 'false');
      group.append(button);
    });
  };

  const hydrateCategories = async root => {
    const categories = ordered(await load('categories'));
    if (!root.isConnected) return;
    if (root.hasAttribute('data-catalog-category-rail')) {
      root.replaceChildren(...categories.map(record => card(record, 'category')).filter(Boolean));
      root.dispatchEvent(new CustomEvent('spirit:catalog:updated', { bubbles: true }));
      return;
    }
    // Keep Shopify's existing active/filter URLs and configured images. Add the
    // missing entries in the same sorted order once later pages are available.
    const current = new Map([...root.children].map(node => [node.dataset.categoryId, node]));
    const nodes = categories.map(record => {
      if (current.has(record.id)) return current.get(record.id);
      const li = document.createElement('li');
      li.dataset.categoryId = record.id;
      const link = document.createElement('a');
      const url = new URL(root.dataset.collectionUrl || '/collections/all', window.location.origin);
      url.searchParams.set('filter.p.m.custom.categories', record.id.startsWith('gid:') ? record.id : `gid://shopify/Metaobject/${record.id}`);
      url.searchParams.set('view', 'category');
      link.href = record.collection || url.href;
      if (record.image) {
        const media = document.createElement('span'); media.className = 'spirit-results-category-nav__media';
        const image = document.createElement('img'); image.src = record.image; image.alt = ''; image.loading = 'lazy'; image.className = 'spirit-results-category-nav__image';
        media.append(image); link.append(media); link.className = 'has-media';
      }
      const label = document.createElement('span'); label.textContent = record.name;
      link.append(label); li.append(link); return li;
    });
    root.replaceChildren(...nodes);
  };

  const hydrateEmotionLinks = async root => {
    const [categories, emotions] = await Promise.all([load('categories'), load('emotions')]);
    if (!root.isConnected || !root.dataset.catalogEventsUrl) return;
    const emotion = emotions.find(record => record.handle === root.dataset.catalogEventEmotion);
    const matches = emotionCategories(emotion, categories);
    if (!matches.length) return;
    const url = new URL(root.dataset.catalogEventsUrl, window.location.origin);
    url.searchParams.set('emotion', emotion.handle);
    url.searchParams.delete('page');
    url.searchParams.set('filter.p.m.custom.categories', matches.map(category => category.id).join(','));
    url.searchParams.set('spirit_categories', matches.map(category => category.handle).join(','));
    root.querySelectorAll('.spirit-results__view-all').forEach(link => { link.href = url.href; });
  };

  const hydrateSearchAlias = async root => {
    const value = root.value;
    const category = (await searchCategories(root.dataset.catalogSearchAlias)).find(record => categoryMatches(record, root.dataset.catalogSearchAlias));
    if (root.isConnected && category && root.value === value) root.value = category.name;
  };

  const populatedTreatment = (record, suppliers, variant, root) => {
    const related = suppliers.filter(supplier => words(supplier.treatments).includes(record.id));
    if (!related.length) return null;
    const node = card(record, variant);
    if (!node) return null;
    node.querySelectorAll('[data-treatment-count]').forEach(count => { count.textContent = String(related.length); });
    node.dataset.providers = String(related.length);
    const avatars = node.querySelector('[data-treatment-avatars]');
    if (avatars) {
      avatars.replaceChildren();
      avatars.setAttribute('aria-label', `${related.length} ${root.dataset.catalogAvailability || 'מטפלים'}`);
      related.map(supplier => card(supplier, 'provider')).filter(Boolean).forEach(provider => {
        const portrait = provider.querySelector('.spirit-directory-provider-card__portrait img');
        if (!portrait || avatars.children.length >= 5) return;
        const link = provider.querySelector('a[href]');
        const avatar = document.createElement(variant === 'result' ? 'li' : (link ? 'a' : 'span'));
        avatar.className = variant === 'result' ? 'spirit-result-treatment-card__avatar' : 'spirit-treatment-card__avatar';
        if (avatar.tagName === 'A') { avatar.href = link.href; avatar.setAttribute('aria-label', provider.dataset.name); }
        avatar.append(portrait.cloneNode(true));
        avatars.append(avatar);
      });
    }
    const cta = root.dataset.catalogCta;
    if (cta) {
      node.querySelectorAll('.spirit-treatment-card__therapists a, .spirit-result-treatment-card__cta').forEach(link => link.setAttribute('aria-label', `${cta}: ${record.name}`));
      const label = node.querySelector('.spirit-treatment-card__link span');
      if (label) label.textContent = cta;
    }
    const availability = node.querySelector('.spirit-result-treatment-card__cta > span:last-child');
    if (availability && root.hasAttribute('data-catalog-availability')) availability.textContent = root.dataset.catalogAvailability;
    const pricePrefix = node.querySelector('.spirit-result-treatment-card__price > span');
    if (pricePrefix && root.hasAttribute('data-catalog-price-prefix')) pricePrefix.textContent = root.dataset.catalogPricePrefix;
    return node;
  };

  const matchesTreatment = (record, root, suppliers, categories, emotions) => {
    if (root.dataset.catalogEmotion) {
      const handles = emotionCategories(emotions.find(item => item.handle === root.dataset.catalogEmotion), categories).map(item => item.id);
      return words(record.emotions).includes(root.dataset.catalogEmotion) || words(record.categories).some(id => handles.includes(id));
    }
    const override = document.querySelector('[data-catalog-treatment-categories]');
    if (override) return words(record.categories).some(id => words(override.dataset.catalogTreatmentCategories).includes(id));
    const selected = words(root.dataset.catalogSelected);
    if (selected.length) return selected.includes(record.id);
    if (root.dataset.catalogTreatments === 'home') return true;
    const term = normalize(root.dataset.catalogTerm);
    const handle = normalize(root.dataset.catalogHandle);
    if (!term && !handle) return false;
    const keys = [record.name, record.code, record.handle].map(normalize).filter(Boolean);
    if (keys.some(key => key === term || key === handle || (term.length > 2 && (key.includes(term) || term.includes(key))))) return true;
    if (categories.some(category => words(record.categories).includes(category.id) && categoryMatches(category, term, handle))) return true;
    return term.length > 2 && suppliers.some(supplier => words(supplier.treatments).includes(record.id) &&
      normalize(supplier.template.content.querySelector('[data-title]')?.dataset.title).includes(term));
  };

  const hydrateTreatments = async root => {
    const [treatments, suppliers, categories, emotions] = await Promise.all([load('treatments'), load('suppliers'), load('categories'), load('emotions')]);
    if (!root.isConnected) return;
    const rail = root.querySelector('[data-spirit-treatments-rail], .spirit-results__rail--treatments');
    if (!rail) return;
    const selected = words(root.dataset.catalogSelected);
    const source = selected.length ? selected.map(id => treatments.find(record => record.id === id)).filter(Boolean) : ordered(treatments);
    const matches = source.filter(record => matchesTreatment(record, root, suppliers, categories, emotions));
    const limit = Number(root.dataset.catalogLimit) || 10;
    let nodes = matches.map(record => populatedTreatment(record, suppliers, root.dataset.catalogTreatments, root)).filter(Boolean).slice(0, limit);
    if (!nodes.length && root.dataset.catalogFallbackProducts) {
      const productIds = words(root.dataset.catalogFallbackProducts);
      nodes = ordered(treatments).filter(record => words(record.products).some(id => productIds.includes(id)))
        .map(record => populatedTreatment(record, suppliers, root.dataset.catalogTreatments, root)).filter(Boolean).slice(0, limit);
    }
    // Preserve a merchant's product fallback when the automatic search is empty.
    if (!nodes.length && rail.children.length && root.dataset.treatmentSource === 'fallback_collection') return;
    rail.replaceChildren(...nodes.map(node => {
      if (rail.tagName !== 'UL') return node;
      const li = document.createElement('li');
      li.className = 'spirit-results__item spirit-results__item--treatment';
      li.append(node);
      return li;
    }));
    root.hidden = nodes.length === 0;
    root.dispatchEvent(new CustomEvent('spirit:catalog:updated', { bubbles: true }));
  };

  const hydrateSuppliers = async root => {
    const [suppliers, treatments, categories] = await Promise.all([load('suppliers'), load('treatments'), load('categories')]);
    if (!root.isConnected) return;
    const selected = words(root.dataset.catalogSelected);
    const role = root.dataset.catalogRole;
    const term = root.dataset.catalogTerm;
    const handle = root.dataset.catalogHandle;
    const source = selected.length ? selected.map(id => suppliers.find(record => record.id === id)).filter(Boolean) : suppliers.filter(record => {
      if (record.id === root.dataset.catalogExclude) return false;
      if (role && role !== 'both' && record.role !== 'both' && record.role !== role && !(['instructor', 'facilitator'].includes(role) && ['instructor', 'facilitator'].includes(record.role))) return false;
      if (!term && !handle) return true;
      return treatments.some(treatment => words(record.treatments).includes(treatment.id) &&
        (categoryMatches(treatment, term, handle) || categories.some(category => words(treatment.categories).includes(category.id) && categoryMatches(category, term, handle))));
    });
    const rail = root.querySelector('[data-provider-rail-track], [data-directory-provider-rail], .spirit-results__provider-grid--suppliers');
    if (!rail) return;
    const nodes = source.slice(0, Number(root.dataset.catalogLimit) || 8).map(record => card(record, root.dataset.catalogSuppliers)).filter(Boolean);
    rail.replaceChildren(...nodes.map(node => {
      if (root.dataset.catalogProviderContext === 'rail') {
        node.classList.remove('spirit-directory-provider-card--grid');
        node.classList.add('spirit-directory-provider-card--rail');
        node.querySelector('.spirit-directory-provider-card__tagline')?.remove();
      }
      const label = node.querySelector('.spirit-directory-provider-card__cta span');
      if (label && root.hasAttribute('data-catalog-cta')) label.textContent = root.dataset.catalogCta;
      const experience = node.querySelector('.spirit-directory-provider-card__meta strong');
      if (experience && root.hasAttribute('data-catalog-experience')) experience.parentElement.replaceChildren(experience, document.createTextNode(` ${root.dataset.catalogExperience}`));
      if (rail.tagName !== 'UL') return node;
      const li = document.createElement('li'); li.className = 'spirit-results__item'; li.append(node); return li;
    }));
    root.hidden = !nodes.length;
  };

  const pending = new WeakSet();
  const retryButtons = new WeakMap();
  const initialize = (scope = document) => {
    const selector = '[data-catalog-treatments], [data-catalog-suppliers], [data-catalog-category-rail], [data-catalog-category-nav], [data-catalog-event-emotion], [data-catalog-search-alias]';
    const roots = [...scope.querySelectorAll(selector)];
    if (scope.matches?.(selector)) roots.push(scope);
    roots.forEach(root => {
      if (pending.has(root)) return;
      pending.add(root);
      root.setAttribute('aria-busy', 'true');
      const hydrate = root.hasAttribute('data-catalog-treatments') ? hydrateTreatments : root.hasAttribute('data-catalog-suppliers') ? hydrateSuppliers : root.hasAttribute('data-catalog-event-emotion') ? hydrateEmotionLinks : root.hasAttribute('data-catalog-search-alias') ? hydrateSearchAlias : hydrateCategories;
      hydrate(root).then(() => {
        retryButtons.get(root)?.remove();
        retryButtons.delete(root);
      }).catch(error => {
        pending.delete(root);
        console.warn('Spirit catalog unavailable; retaining rendered cards', error);
        if (!root.isConnected || retryButtons.has(root)) return;
        const retry = document.createElement('button');
        retry.type = 'button'; retry.className = 'spirit-directory__providers-link'; retry.textContent = retryLabel;
        retry.addEventListener('click', () => initialize(root));
        root.after(retry); retryButtons.set(root, retry);
      }).finally(() => root.removeAttribute('aria-busy'));
    });
  };

  window.SpiritCatalog = { load, enrichDirectory, emotionCategories, searchCategories, categorySearchItems, initialize };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', event => { requests.clear(); initialize(event.target); });
  document.addEventListener('online', () => initialize());
})();
